import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Subject, Subscription, debounceTime, distinctUntilChanged, of, switchMap, takeUntil } from 'rxjs';
import { UserMatterHistoryDto } from '../../models/user-history.models';
import {
  ClientDto,
  ClientSearchResponse,
  ClientService,
} from '../../services/client.service';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { FloatingDialogService } from '../../services/floating-dialog.service';
import {
  ExternalMatterDto,
  MatterService,
} from '../../services/matter.service';
import {
  PageKey,
  UserCookieService,
} from '../../services/user-cookie.service';
import { UserHistoryService } from '../../services/user-history.service';
import { MatterDetailsComponent } from '../matters/matter-details.component';
import { BreadcrumbComponent } from '../shared/breadcrumb/breadcrumb.component';
import { ErrorStateComponent } from '../shared/error-state/error-state.component';

const SEARCH_DEBOUNCE_TIME = 300;
const CLIENT_MATTER_NUMBER_PATTERN = /^\d{4,10}-\d{4,10}$/;

type SearchMode = 'client' | 'billingPartner' | 'matterTitle';
type MatterSortField = 'matterNumber' | 'title' | 'industry' | 'region';

interface MatterSortState {
  field: MatterSortField;
  direction: 'asc' | 'desc';
}

@Component({
  selector: 'app-clients-matters',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    ErrorStateComponent,
    BreadcrumbComponent,
  ],
  templateUrl: './clients-matters.component.html',
  styleUrls: ['./clients-matters.component.scss'],
})
export class ClientsMattersComponent implements OnInit, OnDestroy {
  private clientsService = inject(ClientService);
  private mattersService = inject(MatterService);
  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private userHistoryService = inject(UserHistoryService);
  private userCookieService = inject(UserCookieService);
  private featureFlagService = inject(FeatureFlagService);
  private floatingDialogService = inject(FloatingDialogService);

  isComparisonPopUpsEnabled = false;

  get recentMatterColumns(): string[] {
    return ['clientMatterNumber', 'client', 'title', 'region', 'lastViewed', 'actions'];
  }

  // Data
  clients: ClientDto[] = [];
  clientMatters = new Map<string, ExternalMatterDto[]>();

  // Billing partner client-side pagination (stores full result set)
  private allBillingPartnerClients: ClientDto[] = [];

  // Loading states
  isSearching = false;
  loadingClientDetails = false;
  error: string | null = null;
  showLandingPage = true;

  // Pagination
  pageSize = 10;
  pageIndex = 0;
  totalCount = 0;

  // Expand/collapse
  expandedClients = new Set<string>();
  loadingMatters = new Set<string>();
  matterErrors = new Map<string, string>();

  // Matter sorting per client
  matterSortStates = new Map<string, MatterSortState>();

  // Matter pagination per client
  readonly matterPageSize = 25;
  matterPageSizeOptions = [10, 25, 50];
  matterPageStates = new Map<string, { pageIndex: number; pageSize: number }>();

  // Recently viewed
  recentlyViewedMatters: UserMatterHistoryDto[] = [];
  isLoadingRecentMatters = false;

  // Filter form
  filterForm: FormGroup;

  private pendingSearchRequest: Subscription | null = null;
  private pendingClientDetailRequest: Subscription | null = null;
  private destroy$ = new Subject<void>();

  constructor() {
    const savedSearchMode = this.userCookieService.getFilterValue(
      PageKey.ClientsMatters,
      'searchMode',
    );
    const initialSearchMode: SearchMode =
      savedSearchMode === 'billingPartner' ||
      savedSearchMode === 'matterTitle' ||
      savedSearchMode === 'client'
        ? savedSearchMode
        : 'client';

    this.filterForm = this.formBuilder.group({
      searchTerm: [''],
      searchMode: [initialSearchMode],
    });
  }

  get searchMode(): SearchMode {
    return this.filterForm.get('searchMode')?.value || 'client';
  }

  get searchPlaceholder(): string {
    switch (this.searchMode) {
      case 'billingPartner':
        return 'Search billing partner name or client-matter number';
      case 'matterTitle':
        return 'Search matter title or client-matter number';
      default:
        return 'Search clients or client-matter number';
    }
  }

  get searchLabel(): string {
    switch (this.searchMode) {
      case 'billingPartner':
        return 'Search by billing partner name';
      case 'matterTitle':
        return 'Search by matter title';
      default:
        return 'Search clients by name or number';
    }
  }

  ngOnInit(): void {
    this.setupSearchSubscription();
    this.loadRecentlyViewedMatters();

    this.featureFlagService
      .isFeatureFlagEnabled('comparison-pop-ups')
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.isComparisonPopUpsEnabled = enabled;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearchSubscription(): void {
    this.filterForm
      .get('searchTerm')
      ?.valueChanges.pipe(
        debounceTime(SEARCH_DEBOUNCE_TIME),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((value) => {
        this.handleSearchChange(value?.trim());
      });

    // Re-trigger search when mode changes (if there's a search term)
    this.filterForm
      .get('searchMode')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((mode) => {
        this.userCookieService.setFilterValue(
          PageKey.ClientsMatters,
          'searchMode',
          mode ?? 'client',
        );
        const value = this.filterForm.get('searchTerm')?.value?.trim();
        if (value) {
          this.handleSearchChange(value);
        }
      });
  }

  private handleSearchChange(value: string): void {
    if (!value) {
      this.resetState();
      return;
    }
    this.pageIndex = 0;
    // Detect client-matter number format (XXXXXX-XXXX) regardless of search mode
    if (CLIENT_MATTER_NUMBER_PATTERN.test(value)) {
      this.searchByClientMatterNumber(value);
      return;
    }
    if (this.searchMode === 'billingPartner') {
      this.searchByBillingPartner(value);
    } else if (this.searchMode === 'matterTitle') {
      this.searchByMatterTitle(value);
    } else {
      this.searchClients(value);
    }
  }

  private searchByClientMatterNumber(clientMatterNumber: string): void {
    this.cancelPendingRequest();
    this.isSearching = true;
    this.error = null;
    this.showLandingPage = false;
    this.expandedClients.clear();
    this.clientMatters.clear();
    this.matterSortStates.clear();
    this.matterPageStates.clear();

    this.pendingSearchRequest = this.mattersService
      .getMatter(clientMatterNumber)
      .pipe(
        switchMap((matter) => {
          return this.clientsService
            .getClientsFromList([matter.clientNumber])
            .pipe(
              switchMap((response) => of({ matter, clients: response.clients })),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ matter, clients }) => {
          this.clients = clients;
          this.isSearching = false;
          this.pendingSearchRequest = null;

          if (clients.length > 0) {
            const client = clients[0];
            this.clientMatters.set(client.clientNumber, [matter]);
            this.expandedClients.add(client.clientNumber);
          }
        },
        error: () => {
          this.error = 'Matter not found. Please check the number and try again.';
          this.isSearching = false;
          this.clients = [];
          this.pendingSearchRequest = null;
        },
      });
  }

  private searchClients(searchTerm: string): void {
    this.cancelPendingRequest();
    this.isSearching = true;
    this.error = null;
    this.showLandingPage = false;

    this.pendingSearchRequest = this.clientsService
      .searchClients(searchTerm, undefined, undefined, this.pageSize, this.pageIndex + 1)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ClientSearchResponse) => {
          this.clients = response.clients;
          this.totalCount = response.totalRecords;
          this.isSearching = false;
          this.pendingSearchRequest = null;
          // Collapse all and clear cached matters on new search
          this.expandedClients.clear();
          this.clientMatters.clear();
          this.matterSortStates.clear();
          this.matterPageStates.clear();
        },
        error: () => {
          this.error = 'Failed to search clients. Please try again.';
          this.isSearching = false;
          this.clients = [];
          this.totalCount = 0;
          this.pendingSearchRequest = null;
        },
      });
  }

  private searchByBillingPartner(searchTerm: string): void {
    this.cancelPendingRequest();
    this.isSearching = true;
    this.error = null;
    this.showLandingPage = false;
    this.expandedClients.clear();
    this.clientMatters.clear();
    this.matterSortStates.clear();
    this.matterPageStates.clear();
    this.loadingClientDetails = false;

    this.pendingSearchRequest = this.mattersService
      .getMattersByBillingPartner(searchTerm)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const matters = response.matters;
          this.pendingSearchRequest = null;

          if (matters.length === 0) {
            this.isSearching = false;
            this.clients = [];
            this.totalCount = 0;
            return;
          }

          // Show results immediately with placeholder clients
          const uniqueClientNumbers = [
            ...new Set(matters.map((m) => m.clientNumber)),
          ];
          const placeholderClients = uniqueClientNumbers.map((clientNumber) => ({
            clientNumber,
            clientName: '',
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: '',
            clientStatus: '',
            industry: '',
            industryDescription: '',
            location: '',
            officeDescription: '',
            openDate: '',
          }));

          // Pre-populate matters grouped by client number
          for (const client of placeholderClients) {
            const clientMatters = matters.filter(
              (m: ExternalMatterDto) => m.clientNumber === client.clientNumber,
            );
            this.clientMatters.set(client.clientNumber, clientMatters);
            if (clientMatters.length > 0) {
              this.expandedClients.add(client.clientNumber);
            }
          }

          // Client-side pagination with placeholders
          this.allBillingPartnerClients = placeholderClients;
          this.totalCount = placeholderClients.length;
          this.paginateBillingPartnerResults();
          this.isSearching = false;
          this.loadingClientDetails = true;

          // Step 3: Fetch full client details in background
          this.pendingClientDetailRequest = this.clientsService
            .getClientsFromList(uniqueClientNumbers)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (response) => {
                this.allBillingPartnerClients = this.allBillingPartnerClients.map((placeholder) => {
                  const fullClient = response.clients.find(
                    (c) => c.clientNumber === placeholder.clientNumber,
                  );
                  return fullClient || placeholder;
                });
                this.paginateBillingPartnerResults();
                this.loadingClientDetails = false;
                this.pendingClientDetailRequest = null;
              },
              error: () => {
                this.loadingClientDetails = false;
                this.pendingClientDetailRequest = null;
              },
            });
        },
        error: () => {
          this.error =
            'Failed to search by billing partner. Please try again.';
          this.isSearching = false;
          this.clients = [];
          this.totalCount = 0;
          this.pendingSearchRequest = null;
        },
      });
  }

  private searchByMatterTitle(searchTerm: string): void {
    this.cancelPendingRequest();
    this.isSearching = true;
    this.error = null;
    this.showLandingPage = false;
    this.expandedClients.clear();
    this.clientMatters.clear();
    this.matterSortStates.clear();
    this.matterPageStates.clear();
    this.loadingClientDetails = false;

    // Search matters by title, then progressively display results
    this.pendingSearchRequest = this.mattersService
      .searchMatters(searchTerm, undefined, undefined, undefined, undefined, this.pageSize, this.pageIndex + 1)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.totalCount = response.totalRecords;
          const matters = response.matters;
          this.pendingSearchRequest = null;

          if (matters.length === 0) {
            this.clients = [];
            this.isSearching = false;
            return;
          }

          // Show results immediately with placeholder clients
          const uniqueClientNumbers = [
            ...new Set(matters.map((m) => m.clientNumber)),
          ];
          this.clients = uniqueClientNumbers.map((clientNumber) => ({
            clientNumber,
            clientName: '',
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: '',
            clientStatus: '',
            industry: '',
            industryDescription: '',
            location: '',
            officeDescription: '',
            openDate: '',
          }));

          // Pre-populate matters grouped by client number
          for (const client of this.clients) {
            const clientMatters = matters.filter(
              (m: ExternalMatterDto) => m.clientNumber === client.clientNumber,
            );
            this.clientMatters.set(client.clientNumber, clientMatters);
            if (clientMatters.length > 0) {
              this.expandedClients.add(client.clientNumber);
            }
          }

          this.isSearching = false;
          this.loadingClientDetails = true;

          // Fetch full client details in background
          this.pendingClientDetailRequest = this.clientsService
            .getClientsFromList(uniqueClientNumbers)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (response) => {
                this.clients = this.clients.map((placeholder) => {
                  const fullClient = response.clients.find(
                    (c) => c.clientNumber === placeholder.clientNumber,
                  );
                  return fullClient || placeholder;
                });
                this.loadingClientDetails = false;
                this.pendingClientDetailRequest = null;
              },
              error: () => {
                this.loadingClientDetails = false;
                this.pendingClientDetailRequest = null;
              },
            });
        },
        error: () => {
          this.error =
            'Failed to search matters. Please try again.';
          this.isSearching = false;
          this.clients = [];
          this.totalCount = 0;
          this.pendingSearchRequest = null;
        },
      });
  }

  private cancelPendingRequest(): void {
    if (this.pendingSearchRequest) {
      this.pendingSearchRequest.unsubscribe();
      this.pendingSearchRequest = null;
    }
    if (this.pendingClientDetailRequest) {
      this.pendingClientDetailRequest.unsubscribe();
      this.pendingClientDetailRequest = null;
    }
    this.loadingClientDetails = false;
  }

  private resetState(): void {
    this.cancelPendingRequest();
    this.showLandingPage = true;
    this.isSearching = false;
    this.error = null;
    this.clients = [];
    this.expandedClients.clear();
    this.clientMatters.clear();
    this.matterSortStates.clear();
    this.matterPageStates.clear();
    this.allBillingPartnerClients = [];
    this.pageIndex = 0;
    this.totalCount = 0;
  }

  // Public methods

  onEnterKeySearch(): void {
    const value = this.filterForm.get('searchTerm')?.value?.trim();
    if (value) {
      this.handleSearchChange(value);
    }
  }

  clearSearch(): void {
    this.filterForm.get('searchTerm')?.reset();
    this.resetState();
  }

  resetFilters(): void {
    this.filterForm.patchValue({
      searchTerm: '',
      searchMode: 'client',
    });
    this.resetState();
  }

  hasFiltersChanged(): boolean {
    const searchTerm = this.filterForm.get('searchTerm')?.value;
    const searchMode = this.filterForm.get('searchMode')?.value;
    return !!searchTerm || searchMode !== 'client';
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    const value = this.filterForm.get('searchTerm')?.value?.trim();
    if (!value) return;

    if (this.searchMode === 'billingPartner') {
      this.paginateBillingPartnerResults();
    } else if (this.searchMode === 'matterTitle') {
      this.searchByMatterTitle(value);
    } else {
      this.searchClients(value);
    }
  }

  private paginateBillingPartnerResults(): void {
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    this.clients = this.allBillingPartnerClients.slice(start, end);
  }

  retry(): void {
    const value = this.filterForm.get('searchTerm')?.value?.trim();
    if (value) {
      this.handleSearchChange(value);
    }
  }

  openMatterInFloatingDialog(matter: ExternalMatterDto, event: MouseEvent): void {
    event.stopPropagation();
    this.floatingDialogService.open(
      MatterDetailsComponent,
      matter.matterName,
      { clientMatterNumber: matter.clientMatterNumber },
    );
  }

  navigateToMatter(clientMatterNumber: string, event: MouseEvent): void {
    event.stopPropagation();
    this.router.navigate(['/matters', clientMatterNumber]);
  }

  // Expand/collapse with on-demand matter loading

  toggleClient(client: ClientDto, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    const clientNumber = client.clientNumber;

    if (this.expandedClients.has(clientNumber)) {
      this.expandedClients.delete(clientNumber);
    } else {
      this.expandedClients.add(clientNumber);
      // Load matters if not already loaded
      if (!this.clientMatters.has(clientNumber)) {
        this.loadMattersForClient(clientNumber);
      }
    }
  }

  isExpanded(client: ClientDto): boolean {
    return this.expandedClients.has(client.clientNumber);
  }

  isLoadingMattersFor(clientNumber: string): boolean {
    return this.loadingMatters.has(clientNumber);
  }

  getMatterError(clientNumber: string): string | null {
    return this.matterErrors.get(clientNumber) || null;
  }

  getMattersForClient(clientNumber: string): ExternalMatterDto[] {
    return this.clientMatters.get(clientNumber) || [];
  }

  private loadMattersForClient(clientNumber: string): void {
    this.loadingMatters.add(clientNumber);
    this.matterErrors.delete(clientNumber);

    this.mattersService
      .searchMatters(undefined, undefined, clientNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.clientMatters.set(clientNumber, response.matters);
          this.loadingMatters.delete(clientNumber);
        },
        error: () => {
          this.matterErrors.set(clientNumber, 'Failed to load matters.');
          this.loadingMatters.delete(clientNumber);
        },
      });
  }

  retryLoadMatters(clientNumber: string, event: Event): void {
    event.stopPropagation();
    this.loadMattersForClient(clientNumber);
  }

  // Matter sorting

  onMatterSortChange(clientNumber: string, field: MatterSortField): void {
    const currentState = this.matterSortStates.get(clientNumber);
    let newDirection: 'asc' | 'desc' = 'asc';

    if (currentState && currentState.field === field) {
      newDirection = currentState.direction === 'asc' ? 'desc' : 'asc';
    }

    this.matterSortStates.set(clientNumber, { field, direction: newDirection });
    // Reset matter pagination when sort changes
    this.matterPageStates.delete(clientNumber);
  }

  getMatterSortDirection(
    clientNumber: string,
    field: MatterSortField,
  ): 'asc' | 'desc' | null {
    const state = this.matterSortStates.get(clientNumber);
    return state && state.field === field ? state.direction : null;
  }

  getSortedMatters(clientNumber: string): ExternalMatterDto[] {
    const matters = this.getMattersForClient(clientNumber);
    if (matters.length === 0) return [];

    const sortState = this.matterSortStates.get(clientNumber);
    if (!sortState) {
      // Default sort: openDate descending (newest first), nulls last
      return [...matters].sort((a, b) => {
        const aDate = a.openDate ? new Date(a.openDate).getTime() : null;
        const bDate = b.openDate ? new Date(b.openDate).getTime() : null;
        if (aDate === null && bDate === null) return 0;
        if (aDate === null) return 1;
        if (bDate === null) return -1;
        return bDate - aDate;
      });
    }

    const client = this.clients.find((c) => c.clientNumber === clientNumber);
    const { field, direction } = sortState;
    return [...matters].sort((a, b) => {
      const aValue = this.getMatterFieldValue(a, field, client) || '';
      const bValue = this.getMatterFieldValue(b, field, client) || '';
      const comparison = String(aValue)
        .toLowerCase()
        .localeCompare(String(bValue).toLowerCase());
      return direction === 'asc' ? comparison : -comparison;
    });
  }

  getPagedMatters(clientNumber: string): ExternalMatterDto[] {
    const sorted = this.getSortedMatters(clientNumber);
    const state = this.matterPageStates.get(clientNumber);
    const pageSize = state?.pageSize ?? this.matterPageSize;
    if (sorted.length < pageSize) return sorted;
    const pageIndex = state?.pageIndex ?? 0;
    const start = pageIndex * pageSize;
    return sorted.slice(start, start + pageSize);
  }

  getMatterPageIndex(clientNumber: string): number {
    return this.matterPageStates.get(clientNumber)?.pageIndex ?? 0;
  }

  getMatterPageSize(clientNumber: string): number {
    return this.matterPageStates.get(clientNumber)?.pageSize ?? this.matterPageSize;
  }

  onMatterPageChange(clientNumber: string, event: PageEvent): void {
    this.matterPageStates.set(clientNumber, { pageIndex: event.pageIndex, pageSize: event.pageSize });
  }

  private getMatterFieldValue(
    matter: ExternalMatterDto,
    field: MatterSortField,
    client?: ClientDto,
  ): string {
    switch (field) {
      case 'matterNumber':
        return matter.clientMatterNumber;
      case 'title':
        return matter.matterName;
      case 'industry':
        return client?.industryDescription || '';
      case 'region':
        return client?.state || '';
      default:
        return matter.matterName;
    }
  }

  // Display helpers

  getClientDisplayName(client: ClientDto): string {
    if (client.clientNumber?.trim()) {
      return `#${client.clientNumber} - ${client.clientName}`;
    }
    return client.clientName;
  }

  // Recently viewed

  private loadRecentlyViewedMatters(): void {
    this.isLoadingRecentMatters = true;
    this.userHistoryService
      .getMatterDetailHistory()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (history) => {
          this.recentlyViewedMatters = history;
          this.isLoadingRecentMatters = false;
        },
        error: () => {
          this.recentlyViewedMatters = [];
          this.isLoadingRecentMatters = false;
        },
      });
  }

  getSkeletonRecentMatters(): UserMatterHistoryDto[] {
    return Array.from(
      { length: 5 },
      (_, index) =>
        ({
          id: -(index + 1),
          userId: 0,
          entityType: 'MatterDetail' as any,
          entityId: 0,
          accessedAt: '',
          matter: {
            clientMatterNumber: `loading-${index + 1}`,
            matterName: '',
            matterNumber: '',
            clientNumber: '',
          },
          client: {
            clientNumber: '',
            clientName: '',
            industry: null,
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: '',
            matters: [],
          },
          isSkeleton: true,
        }) as UserMatterHistoryDto & { isSkeleton: boolean },
    );
  }
}
